import React from 'react';
import { Link } from 'react-router-dom';
import { trackCtaClick } from '../analytics/ctaGoal';
import { Helmet } from 'react-helmet-async';
import RouteSeo from '../seo/RouteSeo';
import '../App.css';

/**
 * The assistant's own page (#800).
 *
 * On /features the assistant gets one section out of seven — five bullets for the
 * capability with the largest surface area in the product, below the fold on a page
 * people have to find first. It is also what the Instagram ad runs on, and one of the
 * three rows /compare marks as something no competitor offers.
 *
 * Every claim here is grounded in the desktop source rather than inferred:
 *   - the tool counts come from ChapteryToolCatalog.cs (19) and CloudCatalog.cs (34)
 *   - the provider list from AssistantPanelViewModel.Providers
 *   - "only when you ask" from ChapteryAssistant.BuildOutgoingAsync
 *   - the care-with-prose behaviour from ChapteryAssistant.DefaultSystemPrompt
 *
 * Deliberately absent: anything about MCP. src/Chaptery.Mcp is a real working stdio
 * server, but it is internal tooling — solution-only, no release script publishes it,
 * and it is not in the shipped package. It is not a customer capability, so it is not
 * marketing copy.
 */

const AssistantPage: React.FC = () => (
  <>
    <RouteSeo path="/features/assistant" />
    <Helmet>
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Chaptery', item: 'https://trychaptery.com' },
          { '@type': 'ListItem', position: 2, name: 'Features', item: 'https://trychaptery.com/features' },
          { '@type': 'ListItem', position: 3, name: 'Assistant', item: 'https://trychaptery.com/features/assistant' },
        ],
      })}</script>
    </Helmet>

    <div className="features-page assistant-page">

      {/* Hero.
          An earlier version opened "It doesn't suggest the change. It makes it." — which
          read as an assistant with initiative, deciding things on the author's behalf.
          That is the opposite of what this is. It is a second way to drive the app: every
          instruction is the author's, and the assistant does the navigating and the
          typing. The contrast to draw is not "AI vs no AI" — every competitor has a chat
          box now — but asking for something versus going and finding it yourself. */}
      <section className="features-hero">
        <div className="container">
          <div className="features-tag">The Assistant</div>
          <h1>Say it, instead of clicking through it</h1>
          <p className="features-hero-sub">
            Everything the assistant does, you could do yourself: in the editor, in the
            World Builder, on the storyboard. It just saves you the trip. You decide what
            happens and you say so in a sentence; it handles the menus, the forms and the
            typing.
          </p>
          <div className="hero-actions">
            <Link to="/register" className="btn-primary-large" onClick={() => trackCtaClick('assistant')}>
              Start Free Trial
            </Link>
            <Link to="/features" className="btn-secondary-large">All Features</Link>
          </div>
        </div>
      </section>

      {/* The worked example. This is the page's spine: the manuscript is a local file and
          the world is a cloud API, and a single request crosses that line without the
          author ever learning it exists. */}
      <section className="features-split features-split--alt">
        <div className="container">
          {/* "An afternoon" rather than a number of minutes. An earlier draft of this
              heading said "forty minutes of clicking", which is a fabricated measurement
              — the same class of claim as the priority support line removed in #790.
              This phrasing is plainly idiomatic, and matches the /features section it
              expands on. */}
          <h2 className="asst-section-heading">One sentence instead of an afternoon of forms</h2>
          <div className="asst-demo">
            <p className="asst-demo-ask">
              <span className="asst-demo-label">You ask</span>
              Pull the characters out of the first four chapters and build them in my
              World Builder.
            </p>
            {/* Paired with the "You ask" label above. You ask, it does — the two halves
                stated plainly, so the list below reads as work carried out on request
                rather than steps the assistant decided to take. */}
            <p className="asst-demo-label asst-demo-label--standalone">It does</p>
            <ol className="asst-demo-steps">
              <li>
                <strong>It reads the chapters.</strong> The actual prose in the book you
                have open, not a summary you pasted in.
              </li>
              {/* This said "so the cast you built last week doesn't come back as
                  duplicates". There is no de-duplication: chaptery_create_character
                  merges only on an identical entityId, so the same NAME creates a second
                  character. What is real is that it reads the existing world first. That
                  is what this now claims. (Review of #800.) */}
              <li>
                <strong>It reads the world you've already built.</strong> So it's adding
                to the cast you have, not starting from an empty page.
              </li>
              <li>
                <strong>It creates the ones that are missing.</strong> Name, description,
                age, role, motivation, the flaw that drives them. All of it is filled from the text,
                and left blank where the text doesn't say.
              </li>
              <li>
                <strong>It connects them.</strong> To the book, and to each other (ally,
                mentor, sister), so the relationship graph has something to draw.
              </li>
            </ol>
            <p className="asst-demo-note">
              Your manuscript is the book you have open in Chaptery. Your world lives in
              the cloud, so you can pull it up in any browser. That request crossed
              between the two: one place to ask, instead of two systems to go and learn.
            </p>
          </div>
        </div>
      </section>

      {/* What it can reach. The number is worth stating plainly because "an AI assistant"
          now says nothing — everything has one. Fifty-three specific actions says
          something. Counts verified against the catalogues, not estimated. */}
      <section className="features-split">
        <div className="container">
          <h2 className="asst-section-heading">Fifty-three things you can ask it to do</h2>
          <p className="asst-section-sub">
            Not fifty-three things it can talk about. These are real controls in
            Chaptery, the same ones you'd use by hand. It works them when you ask, and
            only then.
          </p>
          <div className="asst-reach">
            <div className="asst-reach-col">
              <h3>Your manuscript</h3>
              <p className="asst-reach-sub">The book you have open in Chaptery</p>
              <ul className="features-list">
                <li>Read the outline, any chapter, any scene</li>
                <li>Create, rename, reorder and delete chapters</li>
                <li>Set a chapter as prologue, epilogue, foreword or dedication</li>
                <li>Revise a passage in place, or append to the end</li>
                <li>Draft and weave in scenes</li>
                <li>Pull up your notes, and save the book</li>
              </ul>
            </div>
            <div className="asst-reach-col">
              <h3>Your story world</h3>
              <p className="asst-reach-sub">Synced, and open in any browser</p>
              <ul className="features-list">
                <li>Characters: role, arc, motivation, voice and flaw</li>
                <li>Locations: climate, terrain, government, customs, current state</li>
                <li>Events: when they happen, what causes them, what follows</li>
                <li>Relationships between any two of them, in either direction</li>
                <li>Storyboard notes, coloured and linked to the cast</li>
                <li>Storyboard frames: Act One, the midpoint, the fall</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Provider choice. Precision matters here: with a local model the prose never
          reaches an AI provider. World operations still reach OUR API, because that is
          where the author's world already lives and syncs — claiming "nothing leaves your
          machine" would be the tricky kind of true. */}
      <section className="features-split features-split--highlight">
        <div className="container features-split-inner">
          <div className="features-split-text">
            <div className="features-tag features-tag--orange">Your provider</div>
            <h2>You choose the model. You hold the key.</h2>
            {/* #1025. The owner's words, approved verbatim, and the same paragraph that
                opens the assistant section on /features: there is no model inside
                Chaptery, so the one chosen here is the only one there is. */}
            <p>
              <strong>Chaptery has no AI built in.</strong> The assistant does nothing until
              you connect it to an AI service you choose. Once it's connected, it's designed
              only to automate tasks in Chaptery.
            </p>
            <p>
              Chaptery doesn't resell you tokens with a margin on top. Point the assistant
              at your own account and you pay the provider directly, at their price, with
              your own limits.
            </p>
            {/* An earlier draft added "any OpenAI-compatible endpoint, so LM Studio and
                OpenRouter work too". The engine would honour it — OpenAIChatClientFactory
                reads a base URL — but there is no way for a customer to set one:
                AssistantPanel.axaml renders a provider dropdown, an API key box and a
                model box, and AssistantPanelViewModel derives the endpoint from the
                chosen provider, overwriting anything hand-edited into preferences.json.
                A capability reachable only by editing a JSON file that the next Save
                clobbers is not a capability we can sell. (Review of #800.) */}
            <ul className="features-list">
              <li><strong>OpenAI</strong> or <strong>Claude</strong>: paste your API key and pick a model</li>
              <li><strong>Ollama</strong>, running on your own machine: no key, no account, no per-word cost</li>
              <li>Switch between them whenever you like; the assistant works the same way</li>
            </ul>
            <p className="features-callout">
              Running a local model means your chapters are never sent to an AI provider
              at all.
            </p>
          </div>
          <div className="features-split-visual">
            <picture>
              <source media="(max-width: 700px)" srcSet="/product/assistant-mobile.png" width="350" height="651" />
              <img
                src="/product/assistant.png"
                alt="The Chaptery Assistant in the side panel next to the open manuscript, counting how often the author used &quot;said&quot; and its synonyms"
                className="features-screenshot"
                width="520"
                height="232"
                loading="lazy"
                decoding="async"
              />
            </picture>
          </div>
        </div>
      </section>

      {/* What it is like to work with.
          Two of these four used to be framed as reassurances about damage — one headed
          "It's told never to leave a placeholder", which put the failure in the reader's
          head in order to deny it, and one about replacing a passage rather than a whole
          chapter, which is an implementation detail dressed as a benefit. Both now show
          the work instead: a question across the whole manuscript, and a request that
          lands on the world rather than the prose. The honest caveat about mistakes
          stays, at the end, where it does not have to be argued for. */}
      <section className="features-split features-split--alt">
        <div className="container">
          <h2 className="asst-section-heading">What it's like to work with</h2>
          <p className="asst-section-sub">
            It sits beside the writing, in the same window as the chapter you're drafting.
          </p>
          <div className="asst-care">
            <div className="asst-care-item">
              <h3>Ask it about the whole book</h3>
              <p>
                How many times did you write "said", and what did you reach for instead?
                It reads across every chapter and answers. That's a self-editing pass that
                otherwise means a search, a tally, and a lot of scrolling.
              </p>
            </div>
            <div className="asst-care-item">
              <h3>It reaches your world, not just your prose</h3>
              <p>
                Ask it to sort loose ideas into cards on the storyboard, or to fill out a
                character you've only half described. The same conversation reaches the
                manuscript you have open in Chaptery and the world you pull up in a
                browser.
              </p>
            </div>
            <div className="asst-care-item">
              <h3>It waits until you ask</h3>
              <p>
                The Chaptery Assistant only reads what you ask it to. It's not watching
                you write, and it's not scanning your book in the background. It sits
                inactive until you direct it.
              </p>
            </div>
            <div className="asst-care-item">
              {/* Stops at "plain language" on purpose. An earlier version went on to
                  promise no function names, no ids and no JSON — accurate (the system
                  prompt forbids surfacing any of it) but written for someone who knows
                  what those are. A novelist reading this page does not, and naming them
                  only raises a worry they did not have. */}
              <h3>A conversational interface</h3>
              <p>
                It works best when you talk to it the way you'd talk to an assistant
                rather than a terminal. There are no commands to learn. It reports what
                it found and what it did in plain language.
              </p>
            </div>
          </div>
          <p className="asst-care-note">
            It's still an AI, and AI makes mistakes. Before you begin with the Chaptery
            Assistant, save a revision, and the version you had is always one click
            away.
          </p>
        </div>
      </section>

      <section className="features-vs">
        <div className="container features-vs-teaser">
          <h2>The part nobody else does</h2>
          <p>
            Scrivener, Atticus and Vellum have no assistant at all. A chat tab in your
            browser can talk about your book, but it can't work the app for you. See it
            side by side.
          </p>
          <Link to="/compare" className="btn-secondary-large">See the Full Comparison →</Link>
        </div>
      </section>

      <section className="features-cta">
        <div className="container">
          <h2>Try it on your own book</h2>
          <p>Thirty days, every feature, one book. No credit card.</p>
          <div className="hero-actions">
            <Link to="/register" className="btn-primary-large" onClick={() => trackCtaClick('assistant-footer')}>
              Start Free Trial
            </Link>
            <Link to="/pricing" className="btn-secondary-large">View Pricing</Link>
          </div>
        </div>
      </section>

    </div>
  </>
);

export default AssistantPage;
